import { computed, getCurrentInstance, onMounted, ref, toRef, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { Route } from 'vue-router';
import { isDangerSign } from '@shared/clinical-logic/helpers/dangerSigns';
import { TRIAGE_ORDER } from '@/features/overall-assessment/constants/triage.constants';
import { useOverallAssessmentStore } from '@/features/overall-assessment/stores/overallAssessment';
import { useDangerSignStore } from '@/features/danger-signs/stores/dangerSign.store';
import { useMrxStore } from '@/stores/mrx';
import { useTrackStore } from '@/stores/track';
import { assess as runAssess, useDeviceMRX } from '@/utils/mrxEngine';
import { isPositiveDangerSign } from '@/utils/carePlanScope';
import {
  isStoppedEarly,
  normalizeTriage,
  riskAssessmentSection,
  STOPPED_EARLY_NOTE_TOKEN,
} from '../utils/risk-assessment-section';
import type { AssessedCondition, ContentItem, DisabledItem, Triage } from '../types/overall-assessment.types';

type Next = () => void;

interface ClinicalTrack {
  name?: string;
}

interface AnalyticsProxy {
  $online?: boolean;
  $track: (event: string, payload?: Record<string, unknown>) => void;
  $analyticsEvents: Record<string, string>;
  $te: (token: string) => boolean;
  icon: (triage: Triage) => { color: string };
  riskAssessmentToken: (name: string, severity: string) => string;
}

export interface TriageSection {
  triage: Triage;
  headerToken: string;
  headerIconColor: string;
  contentList: ContentItem[];
  disabledItems: Record<string, DisabledItem>;
  initiallyCollapsed: boolean;
}

export interface RiskAssessmentWorkflow {
  loading: Ref<boolean>;
  valid: Ref<boolean | null>;
  overallTriage: Ref<Triage>;
  conditions: Ref<AssessedCondition[]>;
  showPreTriageDialog: ComputedRef<boolean>;
  selected: Ref<string[]>;
  message: ComputedRef<string>;
  notify: ComputedRef<boolean>;
  connectionLost: ComputedRef<boolean>;
  triageSections: ComputedRef<TriageSection[]>;
  assessmentStopped: ComputedRef<boolean>;
  assess: () => Promise<void>;
  retry: () => Promise<void>;
  acknowledgeTriage: () => void;
  setSelected: (selected: string[]) => void;
  handleBeforeRouteLeave: (to: Route, from: Route, next: Next) => void;
}

export function useRiskAssessmentWorkflow(): RiskAssessmentWorkflow {
  const vm = getCurrentInstance();
  const proxy = vm?.proxy as unknown as AnalyticsProxy | undefined;

  const loading = ref(true);
  const valid = ref<boolean | null>(null);
  const overallTriage = ref<Triage>('none');
  const conditions = ref<AssessedCondition[]>([]);
  const preTriageDialogOpen = ref(true);
  const overallAssessmentStore = useOverallAssessmentStore();
  const dangerSignStore = useDangerSignStore();
  const selected = toRef(overallAssessmentStore, 'riskAssessmentSelection');
  const activeTab = toRef(overallAssessmentStore, 'activeTab');

  const mrxStore = useMrxStore();

  const notification = computed(() => mrxStore.notification);
  const validAssessment = computed(() => Boolean(mrxStore.valid));
  const triageFromBundle = computed<Triage>(() => normalizeTriage(mrxStore.triage));
  const trackStore = useTrackStore();
  const filterConditions = computed(() => trackStore.filterConditions || []);
  const currentTrack = computed(() => trackStore.current as ClinicalTrack | undefined);

  const message = computed(() => `app.notifications.${notification.value}.title`);
  const notify = computed(() => notification.value !== null);
  const connectionLost = computed(() => !useDeviceMRX() && proxy?.$online === false);
  const showPreTriageDialog = computed(() => preTriageDialogOpen.value
    && !loading.value
    && valid.value === true
    && activeTab.value === 'Risk Assessment');

  /*  Conditions of a section, by the section they are filed under rather than by the triage
      the clinical logic recommended. The two differ only for an inconclusive condition, which
      the section places under Inconclusive. */
  const conditionsWithTriageMatch = (triage: Triage): AssessedCondition[] => {
    return conditions.value.filter((condition) => {
      return riskAssessmentSection(condition) === triage && condition.severity !== 'not_assessed';
    });
  };

  const isMandatoryDangerSign = (condition: AssessedCondition): boolean => {
    return isPositiveDangerSign(condition);
  };

  const mandatorySelection = computed(() => conditions.value
    .filter(isMandatoryDangerSign)
    .map(condition => condition.name));

  const normalizeSelection = (nextSelected: string[]): string[] => {
    if (valid.value !== true) return nextSelected;
    return [...new Set([
      ...nextSelected.filter(name => (
        !isDangerSign(name) || mandatorySelection.value.includes(name)
      )),
      ...mandatorySelection.value,
    ])];
  };

  const selectionMatches = (left: string[], right: string[]): boolean => {
    return left.length === right.length && left.every((name, index) => name === right[index]);
  };

  const syncSelection = (nextSelected: string[]): void => {
    mrxStore.select({ type: 'assessment', data: nextSelected });
    mrxStore.updateCareplanShownList(nextSelected);
  };

  const setSelected = (nextSelected: string[]): void => {
    const normalized = normalizeSelection(nextSelected);
    if (selectionMatches(selected.value, normalized)) {
      syncSelection(normalized);
    } else {
      // The synchronous watcher updates the canonical bundle after this write.
      selected.value = normalized;
    }
  };

  const riskAssessmentDetailToken = (name: string, severity: string): string | null => {
    const normalizedSeverity = severity === 'risk_none' ? 'none' : severity;
    const token = `assessment.${name}.${normalizedSeverity}.triage`;

    return proxy?.$te(token) ? token : null;
  };

  /*  A partial condition carries the inconclusive severity, but only a handful of conditions
      have an `… .inconclusive.title` string. Falling back to the condition's own name keeps the
      row labelled: the section header already says the result is inconclusive. */
  const riskAssessmentTitleToken = (condition: AssessedCondition): string => {
    const severityToken = proxy?.riskAssessmentToken(condition.name, condition.severity)
      || `assessment.${condition.name}.${condition.severity}.title`;
    if (condition.assessment_state !== 'partial') return severityToken;

    const nameToken = `assessment.${condition.name}.title`;
    return proxy?.$te(severityToken) ? severityToken : nameToken;
  };

  const formatTriages = (triage: Triage): ContentItem[] => {
    const conditionsWithTriage = conditionsWithTriageMatch(triage);
    const orderedConditions = triage === 'urgent'
      ? [
        ...conditionsWithTriage.filter(isMandatoryDangerSign),
        ...conditionsWithTriage.filter(condition => !isMandatoryDangerSign(condition)),
      ]
      : conditionsWithTriage;

    return orderedConditions.map((condition) => {
      const detailToken = riskAssessmentDetailToken(condition.name, condition.severity);
      const stoppedEarly = isStoppedEarly(condition);

      return {
        name: condition.name,
        headerToken: riskAssessmentTitleToken(condition),
        detailContent: detailToken ? [detailToken] : [],
        triage,
        isNoRisk: triage === 'none',
        expansionKey: `${triage}:${condition.name}:${condition.severity}`,
        noteToken: stoppedEarly ? STOPPED_EARLY_NOTE_TOKEN : undefined,
        isDangerSign: isMandatoryDangerSign(condition),
      };
    });
  };

  const triageSections = computed<TriageSection[]>(() => {
    return TRIAGE_ORDER
      .map((triage) => {
        const contentList = formatTriages(triage);

        return {
          triage,
          headerToken: `triage.${triage}.title`,
          headerIconColor: proxy?.icon(triage).color || 'possible',
          contentList,
          disabledItems: Object.fromEntries(contentList
            .filter(item => mandatorySelection.value.includes(item.name))
            .map(item => [item.name, { token: item.headerToken || '' }])),
          initiallyCollapsed: triage === 'none',
        };
      })
      .filter((section) => section.contentList.length > 0);
  });

  const assess = async (): Promise<void> => {
    const payload = {
      suppressSubmitOnAssessment: true,
      // A stop leaves every question the worker never reached unasked, so the clinical logic
      // must be told: without this flag it scores a partial condition as if it were final.
      isStop: dangerSignStore.stopRequested,
      ...(filterConditions.value.length > 0 ? { conditions: filterConditions.value } : {}),
    };

    const assessedEncounter = await runAssess(payload);

    if (validAssessment.value) {
      valid.value = true;
      conditions.value = ((assessedEncounter as AssessedCondition[] | undefined) || []).filter(
        (encounterItem) => encounterItem.type === 'assessment' && !encounterItem.display_hidden,
      );
      overallTriage.value = triageFromBundle.value;

      proxy?.$track(proxy?.$analyticsEvents.TRIAGE_PANEL_VIEWED || 'TRIAGE_PANEL_VIEWED', {
        track_name: currentTrack.value?.name,
        overall_triage: overallTriage.value,
        total_conditions: conditions.value.length,
        conditions_urgent: conditionsWithTriageMatch('urgent').length,
        conditions_immediate: conditionsWithTriageMatch('immediate').length,
        conditions_standard: conditionsWithTriageMatch('standard').length,
        conditions_inconclusive: conditionsWithTriageMatch('inconclusive').length,
        conditions_none: conditionsWithTriageMatch('none').length,
      });

      setSelected(selected.value);
    } else {
      valid.value = false;
    }

    loading.value = false;
  };

  const retry = async (): Promise<void> => {
    loading.value = true;
    valid.value = null;
    await assess();
  };

  const acknowledgeTriage = (): void => {
    preTriageDialogOpen.value = false;
  };

  const handleBeforeRouteLeave = (_to: Route, _from: Route, next: Next): void => {
    if (valid.value && conditions.value.length > 0 && selected.value.length === 0) {
      proxy?.$track(proxy?.$analyticsEvents.TRIAGE_NO_SELECTION || 'TRIAGE_NO_SELECTION', {
        track_name: currentTrack.value?.name,
        overall_triage: overallTriage.value,
        available_conditions: conditions.value.length,
        available_urgent: conditionsWithTriageMatch('urgent').length,
        available_immediate: conditionsWithTriageMatch('immediate').length,
        available_standard: conditionsWithTriageMatch('standard').length,
        available_inconclusive: conditionsWithTriageMatch('inconclusive').length,
      });
    }

    next();
  };

  watch(selected, (nextSelected) => {
    const normalized = normalizeSelection(nextSelected);
    if (!selectionMatches(nextSelected, normalized)) {
      selected.value = normalized;
      return;
    }
    syncSelection(normalized);
  }, { deep: true, flush: 'sync' });

  watch(activeTab, (tab) => {
    if (tab === 'Risk Assessment') {
      preTriageDialogOpen.value = true;
      setSelected(selected.value);
    }
  });

  onMounted(() => {
    assess().then();
  });

  const assessmentStopped = computed<boolean>(() => dangerSignStore.stopRequested);

  return {
    loading,
    valid,
    overallTriage,
    conditions,
    showPreTriageDialog,
    selected,
    message,
    notify,
    connectionLost,
    triageSections,
    assessmentStopped,
    assess,
    retry,
    acknowledgeTriage,
    setSelected,
    handleBeforeRouteLeave,
  };
}

